import React from 'react';
import { CheckCircle, Clock, FileText, CreditCard, Send, Award } from 'lucide-react';

const PROGRESS_MILESTONES = [
  { key: 'agreement', label: 'Agreement', icon: FileText, threshold: 15 },
  { key: 'documents', label: 'Documents', icon: FileText, threshold: 45 },
  { key: 'final_app', label: 'Final Review', icon: CheckCircle, threshold: 65 },
  { key: 'visa_fee', label: 'Visa Fee', icon: CreditCard, threshold: 80 },
  { key: 'filed', label: 'Application Filed', icon: Send, threshold: 90 },
  { key: 'decision', label: 'Decision', icon: Award, threshold: 100 },
];

export default function CaseProgressBar({ progress = 0, showMilestones = true, compact = false }) {
  const getProgressColor = () => {
    if (progress >= 90) return 'bg-green-500';
    if (progress >= 60) return 'bg-teal-500';
    if (progress >= 30) return 'bg-amber-500';
    return 'bg-slate-400';
  };

  if (compact) {
    return (
      <div className="w-full" data-testid="case-progress-bar-compact">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-slate-600">Progress</span>
          <span className="text-xs font-bold text-teal-600">{progress}%</span>
        </div>
        <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className={`h-full ${getProgressColor()} transition-all duration-500`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="case-progress-bar">
      {/* Progress Header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-700">Case Progress</h3>
        <span className="text-lg font-bold text-teal-600">{progress}%</span>
      </div>

      {/* Progress Bar */}
      <div className="relative">
        <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className={`h-full ${getProgressColor()} transition-all duration-500`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Milestone Markers */}
        {showMilestones && (
          <div className="absolute top-0 left-0 w-full h-full flex items-center">
            {PROGRESS_MILESTONES.map((milestone) => (
              <div 
                key={milestone.key}
                className="absolute transform -translate-x-1/2"
                style={{ left: `${milestone.threshold}%` }}
              >
                <div className={`w-3 h-3 rounded-full border-2 ${
                  progress >= milestone.threshold 
                    ? 'bg-white border-teal-500' 
                    : 'bg-slate-200 border-slate-300'
                }`} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Milestone Labels */}
      {showMilestones && (
        <div className="mt-4 grid grid-cols-6 gap-1">
          {PROGRESS_MILESTONES.map((milestone) => {
            const Icon = milestone.icon;
            const isCompleted = progress >= milestone.threshold;
            const isActive = progress >= milestone.threshold - 15 && progress < milestone.threshold;
            
            return (
              <div 
                key={milestone.key}
                className={`text-center ${
                  isCompleted ? 'text-teal-600' : isActive ? 'text-amber-600' : 'text-slate-400'
                }`}
              >
                <Icon size={16} className="mx-auto mb-1" />
                <span className="text-xs font-medium block truncate">{milestone.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Status Message */}
      <div className="mt-3 text-center">
        <p className="text-sm text-slate-600">
          {progress < 15 && "Pending agreement signature"}
          {progress >= 15 && progress < 45 && "Upload required documents"}
          {progress >= 45 && progress < 65 && "Documents under review"}
          {progress >= 65 && progress < 80 && "Awaiting visa fee payment"}
          {progress >= 80 && progress < 90 && "Application being filed"}
          {progress >= 90 && progress < 100 && "Awaiting visa decision"}
          {progress >= 100 && "Case completed!"}
        </p>
      </div>
    </div>
  );
}
